/**
 * TypeScript types for API responses
 */

export enum UserRole {
  PATIENT = 'PATIENT',
  DOCTOR = 'DOCTOR',
  COORDINATOR = 'COORDINATOR',
  THERAPIST = 'THERAPIST',
  NURSE = 'NURSE',
  PHYSICIAN_ASSISTANT = 'PHYSICIAN_ASSISTANT',
  ADMIN = 'ADMIN',
}

export enum PatientStatus {
  ACTIVE = 'ACTIVE',
  ON_TRACK = 'ON_TRACK',
  MISSED = 'MISSED',
  ESCALATED = 'ESCALATED',
  DISCHARGED = 'DISCHARGED',
  INACTIVE = 'INACTIVE',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
}

export enum AppointmentStatus {
  SCHEDULED = 'Scheduled',
  CONFIRMED = 'Confirmed',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  NO_SHOW = 'No Show',
  RESCHEDULED = 'Rescheduled',
}

export enum AppointmentType {
  TELECONSULTATION = 'Teleconsultation',
  IN_PERSON = 'In-Person',
  FOLLOW_UP = 'Follow-up',
  EMERGENCY = 'Emergency',
  ROUTINE_CHECKUP = 'Routine Checkup',
  PHYSIOTHERAPY = 'Physiotherapy Session',
  LAB_TEST = 'Lab Test',
  IMAGING = 'Imaging/Scan',
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  role: UserRole;
  is_active: boolean;
  is_verified: boolean;
  profile_image?: string;
  created_at: string;
  // Role-specific IDs
  patient_id?: number;
  doctor_id?: number;
  therapist_id?: number;
  coordinator_id?: number;
  nurse_id?: number;
  physician_assistant_id?: number;
}

export interface Patient {
  id: number;
  user_id: number;
  umr_no: string;
  date_of_birth: string;
  age: number;
  gender: Gender;
  blood_group?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  condition?: string;
  diagnosis?: string;
  admission_date?: string;
  transplant_date?: string;
  discharge_date?: string;
  status: PatientStatus;
  adherence_score: number;
  streak_days: number;
  height?: number;
  weight?: number;
  bmi?: number;
  cohort_id?: number;
  assigned_doctor_id?: number;
  assigned_therapist_id?: number;
  assigned_coordinator_id?: number;
  assigned_nurse_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

export interface PatientListResponse {
  total: number;
  page: number;
  page_size: number;
  patients: Patient[];
}

export interface Vitals {
  id: number;
  patient_id: number;
  heart_rate?: number;
  spo2?: number;
  systolic_bp?: number;
  diastolic_bp?: number;
  temperature?: number;
  respiratory_rate?: number;
  weight?: number;
  blood_glucose?: number;
  pain_level?: number;
  notes?: string;
  recorded_at: string;
  location?: string;
}

export interface LabResult {
  id: number;
  patient_id: number;
  test_type: string;
  test_name: string;
  test_date: string;
  result_value?: string;
  result_unit?: string;
  reference_range?: string;
  is_abnormal: number;
  lab_name?: string;
  report_file_url?: string;
  uploaded_at: string;
}

export interface Appointment {
  id: number;
  patient_id: number;
  doctor_id?: number;
  therapist_id?: number;
  appointment_type: AppointmentType;
  status: AppointmentStatus;
  scheduled_date: string;
  duration_minutes: number;
  title: string;
  description?: string;
  location?: string;
  meeting_link?: string;
}

export interface Exercise {
  id: number;
  name: string;
  description?: string;
  instructions: string;
  category: string;
  difficulty: string;
  video_url?: string;
  thumbnail_url?: string;
  duration_seconds?: number;
  repetitions?: number;
  sets?: number;
}

export interface ExercisePlan {
  id: number;
  patient_id: number;
  therapist_id: number;
  exercise_id: number;
  title: string;
  goal?: string;
  start_date: string;
  end_date?: string;
  frequency_per_week: number;
  prescribed_sets?: number;
  prescribed_reps?: number;
  prescribed_duration_seconds?: number;
  therapist_notes?: string;
  is_active: boolean;
  completion_rate: number;
  exercise?: Exercise;
}

export interface AdherenceLog {
  id: number;
  patient_id: number;
  adherence_type: string;
  log_date: string;
  is_completed: boolean;
  was_missed: boolean;
  task_name?: string;
  medication_name?: string;
  dosage?: string;
  prescribed_time?: string;
  actual_time?: string;
  skip_reason?: string;
  notes?: string;
}

export interface AdherenceSummary {
  patient_id: number;
  overall_score: number;
  medication_score: number;
  exercise_score: number;
  home_test_score: number;
  appointment_score: number;
  vitals_score: number;
  current_streak: number;
  last_30_days: {
    total_tasks: number;
    completed_tasks: number;
    completion_rate: number;
  };
}

export interface LowAdherencePatient {
  patient_id: number;
  patient_name: string;
  umr_no: string;
  overall_score: number;
  medication_score: number;
  exercise_score: number;
  home_test_score: number;
  last_7_days_avg: number;
  trend: 'improving' | 'declining' | 'stable';
  status: string;
}

export interface SixMWTResult {
  id: number;
  patient_id: number;
  test_date: string;
  distance_meters: number;
  pre_heart_rate?: number;
  pre_spo2?: number;
  post_heart_rate?: number;
  post_spo2?: number;
  dyspnea_score?: number;
  fatigue_score?: number;
  rest_periods: number;
  notes?: string;
}

export interface SpirometryResult {
  id: number;
  patient_id: number;
  test_date: string;
  fev1_liters?: number;
  fev1_percent_predicted?: number;
  fvc_liters?: number;
  fvc_percent_predicted?: number;
  fev1_fvc_ratio?: number;
  quality_grade?: string;
  interpretation?: string;
}

// ========================================
// AUTHENTICATION TYPES
// ========================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CometchatCreateUserRequest {
  uid: string;
  name: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: UserRole;
  // Optional fields for patient registration
  date_of_birth?: string;
  gender?: Gender;
  address?: string;
  city?: string;
  pincode?: string;
  hospital_id?: number;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

// ==================== Medication Types ====================

export interface MedicationSchedule {
  id: number;
  medication_name: string;
  dosage: string;
  frequency: string;
  frequency_display: string;
  category: string;
  scheduled_times: string[];
  instructions?: string;
  prescribed_by: string;
  prescribed_by_id: number;
  start_date: string;
  end_date?: string;
  is_active: boolean;
}

export interface MedicationLog {
  id: number;
  medication_id: number;
  scheduled_time: string;
  taken_time?: string;
  status: 'taken' | 'missed' | 'skipped' | 'pending';
  date: string;
  notes?: string;
  logged_at: string;
}

export interface MedicationAdherenceSummary {
  today_adherence: number;
  week_adherence: number;
  month_adherence: number;
  total_scheduled: number;
  total_taken: number;
  total_missed: number;
  total_skipped: number;
}

export interface MedicationsResponse {
  medications: MedicationSchedule[];
  today_logs: MedicationLog[];
  adherence_summary: MedicationAdherenceSummary;
}

// ==================== Symptom Types ====================

export interface SymptomLog {
  id: number;
  patient_id: number;
  date: string;
  symptoms: {
    breathlessness: number;
    energy_level: number;
    cough: number;
    sleep_quality: number;
    chest_pain: number;
    fatigue: number;
  };
  overall_score: number;
  severity: 'mild' | 'moderate' | 'significant' | 'severe';
  triggers?: string[];
  additional_notes?: string;
  recorded_at: string;
}

export interface SymptomTrends {
  week_average_score: number;
  month_average_score: number;
  most_common_triggers: string[];
}

export interface SymptomsHistoryResponse {
  symptoms: SymptomLog[];
  trends: SymptomTrends;
}

// ==================== Notification Types ====================

export interface Notification {
  id: number;
  notification_type: string;
  priority: string;
  title: string;
  message: string;
  data?: any;
  action_url?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export interface NotificationsListResponse {
  notifications: Notification[];
  unread_count: number;
}